// "Who gets what": the people who receive a client's confirmations, invoices and receipts (client_document_recipients).
// Shared by the client app (setup step 5, Profile > Billing) and Central Command (contact card). The database enforces the
// same rules; these helpers give a friendly message first and build the default choices.
import { normalizeEmail, normalizeMobile } from './clientContactChange.js';

export const MAX_RECIPIENTS = 5;

const blank = (value) => !String(value ?? '').trim();

// Defaults from the design: the client has all three switched on and pricing ticked. When someone else handles billing,
// the client gets confirmations only (no pricing) and the billing contact gets all three with pricing.
export function defaultRecipients({ client, billing = null }) {
    const self = {
        is_self: true, is_billing_contact: false,
        first_name: client.first_name || '', last_name: client.last_name || '', email: client.email || '', phone: client.phone || '',
    };
    if (!billing) {
        return [{ ...self, receives_confirmations: true, receives_invoices: true, receives_receipts: true, include_pricing: true }];
    }
    return [
        { ...self, receives_confirmations: true, receives_invoices: false, receives_receipts: false, include_pricing: false },
        {
            is_self: false, is_billing_contact: true,
            first_name: billing.first_name || '', last_name: billing.last_name || '', email: billing.email || '', phone: billing.phone || '',
            receives_confirmations: true, receives_invoices: true, receives_receipts: true, include_pricing: true,
        },
    ];
}

// A new "+ Add another person" card starts with everything off.
export function emptyRecipient() {
    return {
        is_self: false, is_billing_contact: false, first_name: '', last_name: '', email: '', phone: '',
        receives_confirmations: false, receives_invoices: false, receives_receipts: false, include_pricing: false,
    };
}

export function recipientName(person) {
    return `${person.first_name || ''} ${person.last_name || ''}`.trim();
}

// -> { ok: true } or { ok: false, error } in plain words.
export function validateRecipients(list) {
    if (!Array.isArray(list) || list.length === 0) return { ok: false, error: 'Add at least one person.' };
    if (list.length > MAX_RECIPIENTS) return { ok: false, error: `You can add up to ${MAX_RECIPIENTS} people.` };
    if (list.filter((person) => person.is_self).length !== 1) return { ok: false, error: 'The list must include you.' };
    if (list.filter((person) => person.is_billing_contact).length > 1) return { ok: false, error: 'Only one billing contact is allowed.' };
    const seen = new Set();
    for (const person of list) {
        const label = person.is_self ? 'Your' : recipientName(person) || 'Each person’s';
        if (!person.is_self && (blank(person.first_name) || blank(person.last_name))) {
            return { ok: false, error: 'Every person needs a first and last name.' };
        }
        const email = normalizeEmail(person.email);
        if (!email) return { ok: false, error: `${label} email address is not valid.` };
        if (seen.has(email)) return { ok: false, error: 'Each person needs their own email address.' };
        seen.add(email);
        if (!person.is_self && !blank(person.phone) && !normalizeMobile(person.phone)) {
            return { ok: false, error: `${label} phone number needs an area code.` };
        }
    }
    if (!list.some((person) => person.receives_invoices) || !list.some((person) => person.receives_receipts)) {
        return { ok: false, error: 'At least one person must get invoices and at least one must get receipts.' };
    }
    return { ok: true };
}

// The list as bl407_client_setup_save_recipients expects it (phone cleaned to +country format).
export function toRecipientPayload(list) {
    return list.map((person) => ({
        is_self: Boolean(person.is_self),
        is_billing_contact: Boolean(person.is_billing_contact),
        first_name: String(person.first_name || '').trim(),
        last_name: String(person.last_name || '').trim(),
        email: normalizeEmail(person.email) || String(person.email || '').trim(),
        // +E.164 or nothing: a number that cannot be read is sent as no phone, never as raw text the database would reject.
        phone: blank(person.phone) ? null : normalizeMobile(person.phone),
        receives_confirmations: Boolean(person.receives_confirmations),
        receives_invoices: Boolean(person.receives_invoices),
        receives_receipts: Boolean(person.receives_receipts),
        include_pricing: Boolean(person.include_pricing),
    }));
}
